import React, { useState, useEffect, useRef } from "react";
import { Search, Plus, Pencil, Trash2, X } from "lucide-react";
import { eur, BANDA_BUCKETS, RESULT_BUCKETS, MATRICES } from "../igualdad-data.js";
import { CATALOGOS } from "../igualdad-crud.js";
import { apiGetAll, apiPost, apiPut, apiDelete } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";
import { Status, Toggle } from "../igualdad-components.jsx";
import { IgualdadAgrupacion } from "./igualdad-agrupacion.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Catálogos
   Contenedor de 15 pestañas: los 13 catálogos genéricos que
   resuelve CrudResource (dirigido por descriptor), el editor a
   medida de las matrices de incremento y la vista de trabajo de
   agrupación de conceptos.
   CrudResource y MatrizEditor son internos: nadie más los usa.
   ============================================================ */

const MATRICES_KEY = "matrices-incremento";
const AGRUPACION_KEY = "agrupacion-conceptos";

// Pestañas: catálogos genéricos (CrudResource) + editores a medida.
// La desambiguación de «Departamentos» se aplica SOLO aquí; el descriptor
// de igualdad-crud.js no se toca.
const TABS = [
  ...CATALOGOS.map((c) => ({
    key: c.key,
    label: c.key === "departamentos" ? "Departamentos (Igualdad)" : c.label,
  })),
  { key: MATRICES_KEY, label: "Matrices de incremento" },
  // Vista de trabajo, no CRUD: el catálogo de conceptos sirve para
  // mantenerlos, no para decidir cómo se agrupan.
  { key: AGRUPACION_KEY, label: "Agrupar conceptos" },
];

const ESCENARIOS = [["realista", "Realista"], ["optimista", "Optimista"], ["pesimista", "Pesimista"]];

/* Componente CRUD genérico dirigido por un descriptor (ver igualdad-crud.js).
   Resuelve por sí mismo la lista, las opciones de los campos de relación, y
   las acciones de alta / edición / borrado contra la API. */
function CrudResource({ descriptor, fireToast }) {
  const { session, setClienteById } = useIgualdad();

  const d = descriptor;
  const scoped = d.scope === "politica";
  const refFields = d.fields.filter((f) => f.type === "ref");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState({}); // key de campo ref → lista de opciones
  const [q, setQ] = useState("");

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState(null);

  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  function blank() {
    const o = {};
    for (const f of d.fields) {
      o[f.key] = f.default ?? (f.type === "toggle" ? false : "");
    }
    return o;
  }

  async function load() {
    if (scoped && !session.politicaId) { setRows([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const params = scoped ? { politica_id: session.politicaId } : {};
      const [list, ...opts] = await Promise.all([
        apiGetAll(d.endpoint, params),
        ...refFields.map((f) =>
          apiGetAll(f.ref.endpoint, f.ref.scope === "politica" ? { politica_id: session.politicaId } : {})
        ),
      ]);
      setRows(list);
      const map = {};
      refFields.forEach((f, i) => { map[f.key] = opts[i]; });
      setOptions(map);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  // `watch(() => session.politicaId, ...)` del origen no lleva `immediate: true`,
  // así que no dispara al montar: solo al CAMBIAR el valor. El espejo del valor
  // anterior reproduce esa semántica; sin él, este efecto correría también en el
  // primer commit y duplicaría la ronda de peticiones de `onMounted(load)`.
  const politicaPrev = useRef(session.politicaId);
  useEffect(() => {
    if (politicaPrev.current === session.politicaId) return;
    politicaPrev.current = session.politicaId;
    if (scoped) load();
  }, [session.politicaId]);

  const t = q.trim().toLowerCase();
  const filtered = !t ? rows : rows.filter((r) =>
    d.columns.some((c) => String(cell(r, c) ?? "").toLowerCase().includes(t))
  );

  function refLabel(key, id) {
    const opt = (options[key] || []).find((o) => o.id === id);
    const meta = d.fields.find((f) => f.key === key);
    return opt ? opt[meta?.ref?.label || "nombre"] : "—";
  }

  // Valor mostrado en una celda de la tabla según el tipo de columna.
  function cell(row, col) {
    const v = row[col.key];
    if (col.type === "ref") return refLabel(col.key, v);
    if (col.type === "money") return eur(Number(v) || 0);
    if (col.type === "bool") return v ? "Sí" : "No";
    return v === null || v === undefined || v === "" ? "—" : v;
  }

  function openNew() { setEditing(null); setForm(blank()); setFormErr(null); setModal(true); }

  function openEdit(row) {
    setEditing(row);
    const o = {};
    for (const f of d.fields) {
      const v = row[f.key];
      o[f.key] = f.type === "toggle" ? !!v : (v ?? "");
    }
    setForm(o);
    setFormErr(null);
    setModal(true);
  }

  function payload() {
    const body = {};
    for (const f of d.fields) {
      const v = form[f.key];
      if (f.type === "toggle") body[f.key] = !!v;
      else if (f.type === "number" || f.type === "decimal" || f.type === "ref") {
        body[f.key] = v === "" || v === null ? null : Number(v);
      } else {
        body[f.key] = v === "" ? null : v;
      }
    }
    if (scoped) body.politica_id = session.politicaId;
    return body;
  }

  async function save() {
    if (saving) return;
    setSaving(true); setFormErr(null);
    try {
      if (editing) await apiPut(`${d.endpoint}/${editing.id}`, payload());
      else await apiPost(d.endpoint, payload());
      setModal(false);
      fireToast(editing ? `${d.singular} actualizado.` : `${d.singular} creado.`);
      await load();
      // Recarga politicas y proyectos de la empresa activa (antes reloadPoliticas).
      if (d.refreshSession) await setClienteById(session.clienteId);
    } catch (e) {
      setFormErr(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    } finally { setSaving(false); }
  }

  async function remove() {
    if (deleting || !confirmDel) return;
    setDeleting(true);
    try {
      await apiDelete(`${d.endpoint}/${confirmDel.id}`);
      fireToast(`${d.singular} eliminado.`);
      setConfirmDel(null);
      await load();
      // Recarga politicas y proyectos de la empresa activa (antes reloadPoliticas).
      if (d.refreshSession) await setClienteById(session.clienteId);
    } catch (e) { fireToast(e.message || "No se pudo eliminar."); }
    finally { setDeleting(false); }
  }

  const title = editing ? `Editar ${d.singular.toLowerCase()}` : `Nuevo ${d.singular.toLowerCase()}`;

  return (
    <div className="rb-crud">
      {scoped && !session.politicaId ? (
        <div className="rb-empty">Selecciona una política retributiva para gestionar este catálogo.</div>
      ) : (
        <>
          <div className="rb-toolbar">
            <div className="rb-search"><Search size={15} /><input placeholder={`Buscar ${d.label.toLowerCase()}…`} value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <button className="rb-btn-soft" onClick={openNew}><Plus size={14} /> Nuevo</button>
            <div className="rb-toolbar-r"><span className="rb-count">{filtered.length} {d.label.toLowerCase()}</span></div>
          </div>

          {(loading || error) ? <Status loading={loading} error={error} /> : (
            <div className="rb-card rb-card-flat">
              <table className="rb-table">
                <thead>
                  <tr>
                    {d.columns.map((c) => <th key={c.key} className="rb-l">{c.label}</th>)}
                    <th className="rb-actions-h"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id}>
                      {d.columns.map((c, i) => (
                        <td key={c.key} className={"rb-l " + (i === 0 ? "rb-strong" : "rb-soft")}>
                          {c.type === "bool"
                            ? <span className="rb-chip" style={{ background: r[c.key] ? "var(--success-bg)" : "var(--danger-bg)", color: r[c.key] ? "var(--success-700)" : "var(--danger-700)" }}>{cell(r, c)}</span>
                            : cell(r, c)}
                        </td>
                      ))}
                      <td className="rb-actions">
                        <button className="rb-ico-btn" title="Editar" onClick={() => openEdit(r)}><Pencil size={15} /></button>
                        <button className="rb-ico-btn rb-danger" title="Eliminar" onClick={() => setConfirmDel(r)}><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                  {!filtered.length && (
                    <tr><td className="rb-l rb-soft" colSpan={d.columns.length + 1}>No hay {d.label.toLowerCase()}.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Alta / edición */}
      {modal && (
        <div className="rb-modal-bg" onClick={() => setModal(false)}>
          <div className="rb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">{title}</div><div className="rb-modal-s">{scoped ? `Política ${session.politica?.ejercicio || ""}` : d.label}</div></div>
              <button className="rb-ico" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b">
              {d.fields.map((f) => (
                <React.Fragment key={f.key}>
                  <label>{f.label}{f.required && <span className="rb-req">*</span>}</label>
                  {f.type === "toggle" ? (
                    <div className="rb-togglerow" style={{ marginBottom: 12 }}>
                      <span className="rb-soft">{form[f.key] ? "Sí" : "No"}</span>
                      <Toggle on={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} />
                    </div>
                  ) : f.type === "select" ? (
                    <select value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                      {f.options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
                    </select>
                  ) : f.type === "ref" ? (
                    <select value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                      <option value="">— Selecciona —</option>
                      {(options[f.key] || []).map((o) => <option key={o.id} value={o.id}>{o[f.ref.label]}</option>)}
                    </select>
                  ) : f.type === "textarea" ? (
                    <textarea className="rb-inp rb-inp-gap rb-inp-font" value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={3} />
                  ) : (
                    <input
                      className="rb-inp rb-inp-gap rb-inp-font"
                      type={
                        f.type === "number" || f.type === "decimal" ? "number"
                          : f.type === "email" ? "email"
                            : f.type === "date" ? "date"
                              : "text"
                      }
                      step={f.type === "decimal" ? "0.01" : undefined}
                      value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    />
                  )}
                </React.Fragment>
              ))}
              {formErr && <div className="rb-formerr">{formErr}</div>}
            </div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
              <button className="rb-btn" disabled={saving} onClick={save}>{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación de borrado */}
      {confirmDel && (
        <div className="rb-modal-bg" onClick={() => setConfirmDel(null)}>
          <div className="rb-modal rb-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">Eliminar {d.singular.toLowerCase()}</div><div className="rb-modal-s">Esta acción no se puede deshacer.</div></div>
              <button className="rb-ico" onClick={() => setConfirmDel(null)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b"><p className="rb-soft">¿Seguro que quieres eliminar <b>{confirmDel.nombre || confirmDel.codigo || ("#" + confirmDel.id)}</b>?</p></div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setConfirmDel(null)}>Cancelar</button>
              <button className="rb-btn rb-btn-danger" disabled={deleting} onClick={remove}>{deleting ? "Eliminando…" : "Eliminar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Editor de matrices de incremento: una matriz por escenario (realista /
   optimista / pesimista). Cada matriz es una rejilla 4×6 (buckets de
   resultado A–D × buckets de posición en banda). */
function MatrizEditor({ fireToast }) {
  const { session } = useIgualdad();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [list, setList] = useState([]);

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [form, setForm] = useState(blank);

  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Copia profunda de una matriz semilla {A:[6],B:[6],C:[6],D:[6]}.
  function seed(esc) {
    const src = MATRICES[esc] || MATRICES.realista;
    return Object.fromEntries(RESULT_BUCKETS.map((b) => [b, [...src[b]]]));
  }
  function blank() {
    return { escenario: "realista", valores: seed("realista"), activa: true };
  }

  async function load() {
    if (!session.politicaId) { setList([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      setList(await apiGetAll("/matrices-incremento", { politica_id: session.politicaId }));
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [session.politicaId]);

  function openNew() { setEditing(null); setForm(blank()); setFormErr(null); setModal(true); }

  function openEdit(m) {
    setEditing(m);
    const v = m.valores || {};
    setForm({
      escenario: m.escenario,
      valores: Object.fromEntries(RESULT_BUCKETS.map((b) => [b, (v[b] || seed(m.escenario)[b]).map(Number)])),
      activa: !!m.activa,
    });
    setFormErr(null);
    setModal(true);
  }

  function payload() {
    const valores = {};
    for (const b of RESULT_BUCKETS) valores[b] = form.valores[b].map((n) => Number(n) || 0);
    return {
      politica_id: session.politicaId,
      escenario: form.escenario,
      valores,
      banda_buckets: BANDA_BUCKETS,
      result_buckets: RESULT_BUCKETS,
      activa: form.activa,
    };
  }

  async function save() {
    if (saving) return;
    setSaving(true); setFormErr(null);
    try {
      if (editing) await apiPut(`/matrices-incremento/${editing.id}`, payload());
      else await apiPost("/matrices-incremento", payload());
      setModal(false);
      fireToast(editing ? "Matriz actualizada." : "Matriz creada.");
      await load();
    } catch (e) {
      setFormErr(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    } finally { setSaving(false); }
  }

  async function remove() {
    if (deleting || !confirmDel) return;
    setDeleting(true);
    try {
      await apiDelete(`/matrices-incremento/${confirmDel.id}`);
      fireToast("Matriz eliminada.");
      setConfirmDel(null);
      await load();
    } catch (e) { fireToast(e.message || "No se pudo eliminar."); }
    finally { setDeleting(false); }
  }

  return (
    <div className="rb-matriz">
      {!session.politicaId ? (
        <div className="rb-empty">Selecciona una política retributiva para gestionar las matrices.</div>
      ) : (
        <>
          <div className="rb-toolbar">
            <div className="rb-toolbar-l"><span className="rb-soft">Una matriz por escenario · % de incremento según resultado y posición en banda.</span></div>
            <button className="rb-btn-soft" onClick={openNew}><Plus size={14} /> Nueva matriz</button>
          </div>

          {(loading || error) ? <Status loading={loading} error={error} /> : (
            <>
              {list.map((m) => (
                <div key={m.id} className="rb-card rb-matcard">
                  <div className="rb-mathead">
                    <div><b>{m.escenario_label || m.escenario}</b>{m.activa && <span className="rb-chip rb-chip-ok">Activa</span>}</div>
                    <div className="rb-matacts">
                      <button className="rb-ico-btn" title="Editar" onClick={() => openEdit(m)}><Pencil size={15} /></button>
                      <button className="rb-ico-btn rb-danger" title="Eliminar" onClick={() => setConfirmDel(m)}><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <table className="rb-mattable">
                    <thead><tr><th></th>{(m.banda_buckets || BANDA_BUCKETS).map((b) => <th key={b}>{b}</th>)}</tr></thead>
                    <tbody>
                      {RESULT_BUCKETS.map((r) => (
                        <tr key={r}><th>{r}</th>{(m.valores?.[r] || []).map((val, i) => <td key={i}>{val}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {!list.length && <div className="rb-empty">No hay matrices definidas para esta política.</div>}
            </>
          )}
        </>
      )}

      {modal && (
        <div className="rb-modal-bg" onClick={() => setModal(false)}>
          <div className="rb-modal rb-modal-wide-matriz" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">{editing ? "Editar matriz" : "Nueva matriz"}</div><div className="rb-modal-s">Política {session.politica?.ejercicio}</div></div>
              <button className="rb-ico" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b">
              <label>Escenario</label>
              <select
                value={form.escenario}
                disabled={!!editing}
                onChange={(e) => {
                  // En alta, al cambiar de escenario se resiembran los valores sugeridos.
                  const esc = e.target.value;
                  setForm((f) => ({ ...f, escenario: esc, valores: editing ? f.valores : seed(esc) }));
                }}
              >
                {ESCENARIOS.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
              </select>
              <label style={{ marginTop: 12 }}>Valores (% de incremento)</label>
              <table className="rb-mattable rb-matedit">
                <thead><tr><th>Resultado \ Banda</th>{BANDA_BUCKETS.map((b) => <th key={b}>{b}</th>)}</tr></thead>
                <tbody>
                  {RESULT_BUCKETS.map((r) => (
                    <tr key={r}>
                      <th>{r}</th>
                      {BANDA_BUCKETS.map((_, i) => (
                        <td key={i}>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={form.valores[r][i] ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const n = parseFloat(raw);
                              const val = Number.isNaN(n) ? raw : n;
                              setForm((f) => ({
                                ...f,
                                valores: { ...f.valores, [r]: f.valores[r].map((x, j) => (j === i ? val : x)) },
                              }));
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="rb-togglerow" style={{ marginTop: 14 }}><div><b>Activa</b><p className="rb-fieldnote">La matriz activa es la que se aplica en la simulación de incrementos.</p></div><Toggle on={form.activa} onChange={(v) => setForm((f) => ({ ...f, activa: v }))} /></div>
              {formErr && <div className="rb-formerr">{formErr}</div>}
            </div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
              <button className="rb-btn" disabled={saving} onClick={save}>{saving ? "Guardando…" : (editing ? "Guardar cambios" : "Crear matriz")}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="rb-modal-bg" onClick={() => setConfirmDel(null)}>
          <div className="rb-modal rb-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">Eliminar matriz</div><div className="rb-modal-s">Esta acción no se puede deshacer.</div></div>
              <button className="rb-ico" onClick={() => setConfirmDel(null)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b"><p className="rb-soft">¿Eliminar la matriz <b>{confirmDel.escenario_label || confirmDel.escenario}</b>?</p></div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setConfirmDel(null)}>Cancelar</button>
              <button className="rb-btn rb-btn-danger" disabled={deleting} onClick={remove}>{deleting ? "Eliminando…" : "Eliminar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IgualdadCatalogos() {
  const { fireToast } = useIgualdad();

  const [active, setActive] = useState(CATALOGOS[0].key);
  const current = CATALOGOS.find((c) => c.key === active) || CATALOGOS[0];
  const isMatrices = active === MATRICES_KEY;
  const isAgrupacion = active === AGRUPACION_KEY;

  return (
    <div className="rb-page">
      <h1 className="rb-h1">Catálogos</h1>
      <div className="rb-tabs" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={"rb-tab" + (active === t.key ? " is-active" : "")}
            onClick={() => setActive(t.key)}
          >{t.label}</button>
        ))}
      </div>
      {isAgrupacion
        ? <IgualdadAgrupacion />
        : isMatrices
          ? <MatrizEditor fireToast={fireToast} />
          : <CrudResource key={current.key} descriptor={current} fireToast={fireToast} />}
    </div>
  );
}

export { IgualdadCatalogos };
